import styles from './index.module.less';
import { useRouter } from 'next/router';
import Link from 'next/link';
import { CommonBaseContent } from '@/components/Layouts/CommonBackContent';
import { useTranslation } from 'next-i18next';
import { RollbackOutlined } from '@ant-design/icons';
import Image from 'next/image';
import { AppSettings } from '@/shared/app-common';
import { Checkbox, Col, Input, message, Radio, Row, Space, Spin } from 'antd';
import { useEffect, useState } from 'react';
import { fetchFeedBackData, updateFeedbackData } from '@/lib/service';
import { Answer, FeedbackDetailModel, SurveyResponse } from '@/types/home';
import { useUserProvider } from '@/context/UserProvider';

const FeedbackContent = () => {
  const { t } = useTranslation('common');
  const router = useRouter();
  const [detailModel, setDetailModel] = useState<FeedbackDetailModel>(null);
  const [answers, setAnswers] = useState<Answer[]>([]);
  const [selectedOptions, setSelectedOptions] = useState<{ [key: number]: any[] }>({});
  const [loading, setLoading] = useState(false);
  const { isLogin, onOpenLoginModal, onChangedLoginView } = useUserProvider();

  const loadInfo = async () => {
    fetchFeedBackData()
      .then((res) => {
        if (res?.code === 0) {
          setDetailModel(res?.data);
        }
      })
      .catch((e) => console.log(e));
  };

  useEffect(() => {
    loadInfo();
  }, []);

  const handleSingleChange = (question_id: number, option_id: number, input_content?: string) => {
    setAnswers((prevAnswers) => {
      const newAnswers = prevAnswers.filter((answer) => answer.question_id !== question_id);
      newAnswers.push({
        question_id,
        option_id,
        input_content: input_content ? input_content : '',
      });
      return newAnswers;
    });
    setSelectedOptions((prevState) => ({
      ...prevState,
      [question_id]: [option_id],
    }));
  };

  const handleMultipleChange = (question_id: number, checkedValues: any[]) => {
    setAnswers((prevAnswers) => {
      const newAnswers = prevAnswers.filter((answer) => answer.question_id !== question_id);
      checkedValues.forEach((value) => {
        const [option_id, input_content] = value.split('|');
        newAnswers.push({
          question_id,
          option_id: Number(option_id),
          input_content: input_content ? input_content : '',
        });
      });
      return newAnswers;
    });
    setSelectedOptions((prevState) => ({
      ...prevState,
      [question_id]: checkedValues,
    }));
  };

  const handleInputChange = (question_id: number, input_content: string) => {
    setAnswers((prevAnswers) => {
      const newAnswers = prevAnswers.filter((answer) => answer.question_id !== question_id);
      newAnswers.push({ question_id, input_content });
      return newAnswers;
    });
  };

  const handleSubmit = async () => {
    var allowRequest = true;
    detailModel?.questions.forEach((element) => {
      if (element?.required && !answers.find((answer) => answer.question_id === element.question_id)) {
        allowRequest = false;
        return;
      }
    });

    if (!allowRequest) {
      message.error(t("Please complete all questions marked with an '*'."));
      return;
    }

    if (!isLogin) {
      onChangedLoginView(false);
      onOpenLoginModal(true);
      return;
    }

    const surveyResponse: SurveyResponse = {
      survey_id: detailModel.survey_id,
      answers,
    };
    // 在这里发送数据到后端
    setLoading(true);
    await updateFeedbackData(surveyResponse)
      .then((res) => {
        if (res?.code === 0) {
          message.success(t('submit success'));
          setLoading(false);
          router.push({
            pathname: '/',
            query: { survey_reward: true },
          });
        } else {
          message.error(res?.message);
          setLoading(false);
        }
      })
      .catch((e) => console.log(e));
  };

  return (
    <Spin spinning={loading}>
      <CommonBaseContent style={{ padding: 0, background: 'transparent', minHeight: '100vh' }}>
        <div className={styles.headerContainer}>
          <div>
            <Link href={AppSettings.webHost}>
              <div style={{ height: '25px', display: 'flex', alignItems: 'center' }}>
                <Image
                  className={styles.logo}
                  src={AppSettings.logoPath}
                  alt={AppSettings.name}
                  loading="lazy"
                  width={25}
                  height={25}
                />
                <img src={AppSettings.namePath} alt={AppSettings.name} style={{ height: '25px' }} />
              </div>
            </Link>
          </div>
          <div className={styles.topRightContainer} onClick={() => router.push('/')}>
            <RollbackOutlined />
            <span>{t('Home')}</span>
          </div>
        </div>

        <div className={styles.content}>
          <div className={styles.topContainer}>
            <h1>
              <span>{t('Help us improve')}</span>
            </h1>
            <div className={styles.descContainer}>
              {t('We would love to hear your feedback on this new feature to help us improve and serve you better.')}
            </div>
          </div>

          {detailModel?.questions?.map((item, index) => {
            return (
              <div className={styles.sectionContainer} key={index}>
                <div className={styles.sectionHeader}>
                  <span>{item.required && '*'}</span>
                  <span>{`${index + 1}. ${item?.content}`}</span>
                </div>
                <div className={styles.sectionBody}>
                  {item.type === 'SELECT' ? (
                    <Radio.Group
                      style={{ width: '100%' }}
                      onChange={(e) => {
                        const [option_id, input_content] = e.target.value.split('|');
                        handleSingleChange(item.question_id, Number(option_id), input_content);
                      }}
                    >
                      <Space direction="vertical">
                        {item.options?.map((option) => {
                          return (
                            <Radio
                              style={{ color: 'var(--app-text-color)' }}
                              key={option.option_id}
                              value={`${option.option_id}`}
                            >
                              <span>{option.content}</span>
                              {option.allows_input && (
                                <Input
                                  style={{ marginLeft: '10px', width: '200px' }}
                                  onChange={(e) =>
                                    handleSingleChange(item.question_id, option.option_id, e.target.value)
                                  }
                                />
                              )}
                            </Radio>
                          );
                        })}
                      </Space>
                    </Radio.Group>
                  ) : item.type === 'CHECKBOX' ? (
                    <Checkbox.Group
                      style={{ width: '100%' }}
                      onChange={(checkedValues) => handleMultipleChange(item.question_id, checkedValues)}
                      value={selectedOptions[item.question_id]}
                    >
                      <Row>
                        {item.options?.map((option) => {
                          return (
                            <Col span={24} style={{ marginBottom: '10px' }} key={option.option_id}>
                              <Checkbox value={`${option.option_id}${option.allows_input ? '|user_input' : ''}`}>
                                <span style={{ color: 'var(--app-text-color)' }}>{option.content}</span>
                              </Checkbox>
                              {option.allows_input && (
                                <Input.TextArea
                                  onChange={(e) => {
                                    handleMultipleChange(item.question_id, [
                                      ...selectedOptions[item.question_id].filter(
                                        (val) => !val.startsWith(`${option.option_id}|`)
                                      ),
                                      `${option.option_id}|${e.target.value}`,
                                    ]);
                                  }}
                                  autoSize
                                  style={{
                                    width: '90%',
                                    minHeight: '100px',
                                    padding: '5px',
                                    backgroundColor: 'var(--app-bg-color)',
                                    marginLeft: '20px',
                                    marginTop: '10px',
                                    marginRight: '40px',
                                    color: 'var(--app-text-color)',
                                  }}
                                />
                              )}
                            </Col>
                          );
                        })}
                      </Row>
                    </Checkbox.Group>
                  ) : (
                    <div className={styles.sectionBody}>
                      <textarea
                        onChange={(e) => {
                          handleInputChange(item.question_id, e.target.value);
                        }}
                        name=""
                        id=""
                        style={{
                          width: '100%',
                          minHeight: '100px',
                          color: 'var(--app-text-color)',
                          padding: '5px',
                          backgroundColor: 'var(--app-bg-color)',
                          border: '1px solid #D9D9D9',
                        }}
                      />
                    </div>
                  )}
                </div>
              </div>
            );
          })}

          <div className={styles.bottomContainer}>
            <span onClick={handleSubmit}>{t('Submit Now')}</span>
          </div>
        </div>
      </CommonBaseContent>
    </Spin>
  );
};

export default FeedbackContent;
